/**
 * Guest activity tracker.
 *
 * Fires analytics events to the backend. All calls are fire-and-forget:
 * failures are silently swallowed and never affect the user experience.
 *
 * PRIVACY: Never send SMILES, compound names, target names, InChIKeys,
 * patent numbers, or search query text. Only categories and counts.
 */

import { getStoredToken } from './tokenStorage';
import { safeRandomUUID } from './safeRandomUUID';

const sessionId = safeRandomUUID();

export async function trackEvent(
  actionType: string,
  actionCategory: string,
  pageArea?: string,
  metadata?: Record<string, string | number | boolean>,
) {
  try {
    const token = getStoredToken();
    if (!token) return; // Not logged in

    fetch('/api/v2/activity/track', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${token}`,
      },
      body: JSON.stringify({
        action_type: actionType,
        action_category: actionCategory,
        page_area: pageArea,
        metadata: metadata || {},
        session_id: sessionId,
      }),
      keepalive: true,
    }).catch(() => {});
  } catch {
    // Never throw
  }
}

/** Track a page view. Call from route change handlers. */
export function trackPageView(pageArea: string) {
  trackEvent('page_view', 'view', pageArea);
}

/** Track session end when user leaves the tab. */
export function trackSessionEnd() {
  trackEvent('session_end', 'session', undefined, {
    duration_seconds: Math.round(performance.now() / 1000),
  });
}

/** Get the current session ID (for correlation). */
export function getSessionId() {
  return sessionId;
}

/**
 * Initialize automatic tracking:
 * - Visibility change → session_end
 *
 * Call once at app startup.
 */
export function initActivityTracking() {
  // Track session end when the tab is hidden or closed
  document.addEventListener('visibilitychange', () => {
    if (document.hidden) {
      trackSessionEnd();
    }
  });
}
